"use client";

import { Clock3, Lock, Minus, Plus } from "lucide-react";
import { AppImage as Image } from "@/components/ui/app-image";
import {
  formatClockTime,
  formatDayMonth,
  formatDayMonthTime,
} from "@/lib/dates/relative-time";
import type { AddonImage } from "@/lib/events/addon-types";
import { blurHashToDataUrl } from "@/lib/media/blurhash";
import { cn } from "@/lib/utils";

/**
 * Optional media. Every add-on surface renders correctly with none: rows lose
 * their thumbnail column, rails fall back to a priced list. The box is
 * reserved from stored dimensions so nothing shifts when the bytes land.
 */
export function AddonThumb({
  image,
  alt,
  sizes,
  className,
}: {
  image: AddonImage;
  alt: string;
  sizes: string;
  className?: string;
}) {
  const blur = blurHashToDataUrl(image.blurHash);
  return (
    <span
      className={cn(
        "relative block overflow-hidden bg-black/5 dark:bg-white/5",
        className,
      )}
    >
      <Image
        src={image.url}
        alt={alt}
        fill
        sizes={sizes}
        className="object-cover"
        placeholder={blur ? "blur" : undefined}
        blurDataURL={blur}
      />
    </span>
  );
}

export function formatAddonDeadline(iso: string) {
  return formatDayMonthTime(iso, { weekday: true });
}

/** "Sat, 12:00 pm" — for surfaces that only ever show the coming week. */
export function formatAddonDeadlineShort(iso: string) {
  const day = formatDayMonth(iso, { weekday: true }).split(" ")[0];
  return day ? `${day}, ${formatClockTime(iso)}` : "";
}

/**
 * "Order by Fri, 10 Oct, 12:00 pm" — turns amber inside the last day, because
 * that is when "later" stops being an option.
 */
export function AddonDeadline({
  cutoffIso,
  now,
  className,
  branded = false,
}: {
  cutoffIso: string;
  now: Date | null;
  className?: string;
  /** Inside a brand-themed block: read colours from `--brand-*`. */
  branded?: boolean;
}) {
  const cutoff = Date.parse(cutoffIso);
  const closed = now !== null && now.getTime() >= cutoff;
  const hoursLeft = now ? (cutoff - now.getTime()) / 3_600_000 : null;
  const urgent = hoursLeft !== null && hoursLeft > 0 && hoursLeft <= 24;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
        branded
          ? closed
            ? "bg-[var(--brand-raised)] text-[var(--brand-ink-muted)]"
            : "bg-[var(--brand-highlight)] text-[var(--brand-highlight-ink)]"
          : closed
            ? "bg-black/5 text-text-tertiary dark:bg-white/8 dark:text-white/50"
            : urgent
              ? "bg-amber-500/12 text-amber-700 dark:text-amber-300"
              : "bg-brand/10 text-brand dark:bg-brand/20 dark:text-white/85",
        className,
      )}
    >
      {closed ? (
        <Lock className="size-3.5" aria-hidden />
      ) : (
        <Clock3 className="size-3.5" aria-hidden />
      )}
      {closed
        ? "Pre-orders closed"
        : urgent && hoursLeft !== null && hoursLeft < 6
          ? `Closes in ${Math.max(1, Math.round(hoursLeft))}h`
          : `Order by ${formatAddonDeadline(cutoffIso)}`}
    </span>
  );
}

/**
 * "Add" until there is one, then a stepper in the same footprint — the control
 * never changes size, so a row never reflows under the thumb that tapped it.
 */
export function AddonQuantityControl({
  name,
  quantity,
  disabled,
  onChange,
  className,
  branded = false,
}: {
  name: string;
  quantity: number;
  disabled?: boolean;
  onChange: (next: number) => void;
  className?: string;
  /** Inside a brand-themed block: the brand colour, not the app's. */
  branded?: boolean;
}) {
  if (quantity === 0)
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(1)}
        aria-label={`Add ${name}`}
        className={cn(
          "h-9 w-full rounded-xl border text-sm font-bold shadow-[0_4px_14px_-6px_rgba(15,23,42,0.35)] transition-transform active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50",
          branded
            ? "border-[var(--brand-accent)] bg-[var(--brand-raised)] text-[var(--brand-accent-text)]"
            : "border-black/10 bg-white text-brand dark:border-white/15 dark:bg-neutral-900 dark:text-white",
          className,
        )}
      >
        Add
      </button>
    );
  return (
    <div
      className={cn(
        "grid h-9 w-full grid-cols-[1fr_auto_1fr] items-center overflow-hidden rounded-xl shadow-[0_4px_14px_-6px_rgba(15,23,42,0.45)]",
        branded
          ? "bg-[var(--brand-accent)] text-[var(--brand-accent-ink)]"
          : "bg-brand text-white",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onChange(quantity - 1)}
        aria-label={`Remove one ${name}`}
        className="flex h-full items-center justify-center active:bg-black/10"
      >
        <Minus className="size-4" aria-hidden />
      </button>
      <span
        className="min-w-6 text-center text-sm font-bold tabular-nums"
        aria-live="polite"
      >
        {quantity}
      </span>
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange(quantity + 1)}
        aria-label={`Add another ${name}`}
        className="flex h-full items-center justify-center active:bg-black/10 disabled:opacity-40"
      >
        <Plus className="size-4" aria-hidden />
      </button>
    </div>
  );
}
